import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useGoBack } from '../../hooks/useGoBack'
import { formatEventPeriod } from '../../lib/eventPeriod'
import { CalendarDays, ExternalLink, MapPin, Sparkles, Store as StoreIcon } from 'lucide-react'
import Header from '../../components/Header'
import { useI18n } from '../../i18n/useI18n'
import { api, type StampEventInfo } from '../../services/api'
import { BoothList, JoinCard, PrizeLines, ProgramTitle } from './eventInfoParts'

/**
 * #1898 企画詳細ページ（公開・ログイン不要）。
 *
 * TOP のイベントのカードでは、企画 (スタンプラリー / AR宝探し / ミニゲーム) ごとに
 * 「企画の詳細をみる」を置く。当初はカードに「イベントの詳細をみる」を 1 つだけ
 * 置いていたが、中身はもともと企画 1 つ分の詳細で、どの企画の話か分からなかった。
 *
 * データはイベント詳細と同じ API (`/stamp/events/{企画ID}` は所属イベントを返す) から
 * この企画のぶんだけを取り出す。
 *
 * **イベント (親) へのボタンは置かない。** 自分で選んだ企画のページに、関係の薄い
 * ものが割り込んで見えるため。イベントの詳細は TOP のイベントのカードの最後から辿る。
 */
export default function StampProgramInfo() {
  const { t, locale } = useI18n()
  const goBack = useGoBack('/stamp-rally')
  const { programId } = useParams<{ programId: string }>()
  const [info, setInfo] = useState<StampEventInfo | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!programId) return
    let cancelled = false
    api.getStampEventInfo(programId)
      .then((res) => { if (!cancelled) setInfo(res) })
      .catch(() => { if (!cancelled) setError(t('stampRally.eventInfoFailed')) })
    return () => { cancelled = true }
  }, [programId, t])

  const program = info?.programs.find((p) => p.event.id === programId) ?? null
  // 非公開の企画は API が返さない (同じイベントの公開中の企画だけが並ぶ)
  const failed = error ?? (info && !program ? t('stampRally.eventInfoFailed') : null)

  // #1886 `#booths` 付きで来たら参加ブースまで送る (「ブースはここ」と名指しして配るリンク)。
  // #1898 ブースはスタンプラリーの企画に付くので、イベント詳細ではなくここで受ける。
  const { hash } = useLocation()
  useEffect(() => {
    if (!program || hash !== '#booths') return
    document.getElementById('booths')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [program, hash])

  return (
    <div className="mobile-container min-h-screen bg-warm-white safe-top safe-bottom">
      <Header title={t('stampRally.programInfoTitle')} onBack={goBack} />

      {failed && (
        <p className="px-4 py-10 text-center font-body text-sm text-text-secondary">{failed}</p>
      )}

      {!info && !error && (
        <div className="flex justify-center py-16">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-navy border-t-transparent" />
        </div>
      )}

      {info && program && (
        <div className="flex flex-col gap-4 px-4 pb-24 pt-4 animate-fade-in">
          <div>
            <ProgramTitle program={program} large />
            {/* イベントと同じ説明は出さない (移行前の企画にはイベントの文面が残っていることがある) */}
            {program.event.description && program.event.description !== info.event.description && (
              <p className="mt-1 font-body text-sm text-text-secondary">{program.event.description}</p>
            )}
          </div>

          <div className="flex flex-col gap-2 rounded-2xl bg-white p-4 shadow-card">
            <p className="flex items-center gap-2 font-body text-sm text-text-primary">
              <CalendarDays size={16} className="shrink-0 text-navy" />
              {formatEventPeriod(program.event.startsAt, program.event.endsAt, locale, { month: 'long' })}
            </p>
            {/* #1898 会場があれば会場を出す (地域名だけでは、どこでやるのか分からない) */}
            {info.event.venue ? (
              <p className="flex items-center gap-2 font-body text-sm text-text-primary">
                <MapPin size={16} className="shrink-0 text-navy" />
                {info.event.venue}
              </p>
            ) : (
              info.region?.name && (
                <p className="flex items-center gap-2 font-body text-sm text-text-primary">
                  <MapPin size={16} className="shrink-0 text-navy" />
                  {info.region.name}
                </p>
              )
            )}
            {program.storeCount > 0 && (
              <p className="flex items-center gap-2 font-body text-sm text-text-primary">
                <StoreIcon size={16} className="shrink-0 text-navy" />
                {t('stampRally.eventInfoStores', { count: String(program.storeCount) })}
              </p>
            )}
            <PrizeLines prize={program.prize} lottery={program.lottery} />
          </div>

          {/* 企画の説明。改行をそのまま活かす（運営はプレーンテキストで書くため） */}
          {program.event.overview && program.event.overview !== info.event.overview && (
            <p className="whitespace-pre-wrap font-body text-sm leading-relaxed text-text-primary">
              {program.event.overview}
            </p>
          )}

          {program.event.linkOnly ? (
            // リンクで遊ぶだけの企画。参加の登録は要らない
            program.event.activityUrl && (
              <a
                href={program.event.activityUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 rounded-full bg-yokoso-red py-3.5 font-body text-sm font-medium text-white tap-effect"
              >
                <Sparkles size={18} />
                {program.event.ctaLabel || t('stampRally.linkActivityCta')}
              </a>
            )
          ) : (
            <JoinCard
              regionId={info.region?.id}
              focusId={program.event.id}
              returnTo={`/stamp-rally/program/${program.event.id}`}
              hasLottery={!!program.lottery}
            />
          )}

          {program.kind === 'qr' && (
            <BoothList stores={info.stores.filter((s) => s.eventId === program.event.id)} />
          )}

          {/* 主催者の公式サイトはイベント (親) が持つ。企画に残っている URL は作成時の写しで、
              イベント側で直しても追従しない */}
          {info.event.infoUrl && (
            <a
              href={info.event.infoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="tap-card flex items-center justify-between rounded-2xl bg-white px-4 py-4 shadow-card"
            >
              <span className="font-body text-sm text-text-primary">
                {t('stampRally.eventInfoOfficial')}
              </span>
              <ExternalLink size={16} className="shrink-0 text-text-tertiary" />
            </a>
          )}
        </div>
      )}
    </div>
  )
}
